import { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
	CreateScheduleRepeat,
	DaysOfWeekEnum,
	isDayOfLastWeekOfMonth,
	ScheduleRepeatTypesEnum,
	waitAsync,
} from '@sweatpals/shared';
import { format, getWeekOfMonth } from 'date-fns';
import { map } from 'lodash-es';
import { CoreRadio, CoreRadioGroup, RadioFormLabel } from '../../_core/core-radio';
import { EventDateTimePicker, TextField } from '../..';
import Showable from '../../utils/showable';
import {
	Colors,
	DaysOfWeek,
	DaysOfWeekArray,
	getDefaultMonthlyRepeatValues,
	getDefaultWeeklyRepeatValues,
	maxWeekInterval,
	minWeekInterval,
	ScheduleRepeatOptions,
} from '../../../constants';
import { CoreFormControl } from '../../_core';
import { CoreIconButton } from '../../_core/core-button';
import { useRepeatScheduleModalState } from '../hooks/use-repeat-schedule-modal-state.hook';
import { RightBlackModal } from '../../right-modal';
import { customResetForm } from '../../../utils';

export enum RepeatUntilEnum {
	Forever = 'forever',
	After = 'after',
}

export interface RepeatScheduleModalProps {
	open: boolean;
	data?: CreateScheduleRepeat;
	CalendarIcon?: FC;
	eventStartDate: Date;
	tzid: string;
	onClose?(isDirty?: boolean): void;
	onSubmit?(reepatSchedule: CreateScheduleRepeat): void;
}

export const RepeatScheduleModal: FC<RepeatScheduleModalProps> = props => {
	const {
		data,
		eventStartDate,
		tzid,
		onClose,
		open,
		onSubmit,
		CalendarIcon,
	} = props;

	const [state, setState] = useState<{ endDateType: RepeatUntilEnum }>({
		endDateType: RepeatUntilEnum.Forever,
	});
	const defaultValues = useMemo(
		() => (data && data.typeId !== ScheduleRepeatTypesEnum.Never ? data : getDefaultWeeklyRepeatValues(eventStartDate)),
		[data, eventStartDate]
	);

	const {
		form,
		formData: schedule,
		register,
	} = useRepeatScheduleModalState({
		mode: 'onChange',
		defaultValues: structuredClone(defaultValues),
	});

	const handleClose = useCallback(() => {
		onClose?.(form.formState.isDirty);
	}, [onClose, form]);

	useEffect(() => {
		if (open) {
			customResetForm(form, structuredClone(defaultValues));
			setState({ endDateType: defaultValues?.endDate ? RepeatUntilEnum.After : RepeatUntilEnum.Forever });
		}
	}, [open]);

	const onTypeIdChange = useCallback(
		(typeId: ScheduleRepeatTypesEnum) => {
			switch (typeId) {
				case ScheduleRepeatTypesEnum.Weekly:
					form.reset({ ...getDefaultWeeklyRepeatValues(eventStartDate), typeId });
					break;
				case ScheduleRepeatTypesEnum.Monthly:
					form.reset({ ...getDefaultMonthlyRepeatValues(eventStartDate), typeId });
					break;
				default:
					form.reset({ typeId });
			}
			setState({ endDateType: RepeatUntilEnum.Forever });
		},
		[eventStartDate, form]
	);

	const onDayOfWeekChange = useCallback(
		(day: DaysOfWeekEnum): void => {
			if (schedule.dayOfWeekIds?.includes(day)) {
				form.setValue(
					'dayOfWeekIds',
					schedule.dayOfWeekIds?.filter(id => id !== day)
				);
			} else {
				const newValues = Array.from(new Set([day, ...(schedule.dayOfWeekIds ?? [])])).sort((day1, day2) => {
					const coeff1 = DaysOfWeekArray.findIndex(day => day.id === day1);
					const coeff2 = DaysOfWeekArray.findIndex(day => day.id === day2);

					return coeff1 - coeff2;
				});
				form.setValue('dayOfWeekIds', newValues);
			}
		},
		[schedule.dayOfWeekIds, form]
	);

	const onRepeatUntilChange = useCallback(
		(val: RepeatUntilEnum) => {
			setState(state => ({ ...state, endDateType: val }));

			if (val === RepeatUntilEnum.Forever) {
				form.setValue('endDate', undefined);
			}
		},
		[form]
	);

	const handleSave = async (schedule: CreateScheduleRepeat) => {
		await waitAsync(500);

		onSubmit?.(schedule);
	};

	const getScheduleRepeatTypeLabel = (option: { id: ScheduleRepeatTypesEnum; label: string }) => {
		if (option.id === ScheduleRepeatTypesEnum.Monthly) {
			if (!eventStartDate) return '';

			const isLast = isDayOfLastWeekOfMonth(eventStartDate);
			const day = format(eventStartDate, 'EEEE');
			const weekOfMonth = getWeekOfMonth(eventStartDate);
			const replacements: { [key: number]: string } = {
				1: 'first',
				2: 'second',
				3: 'third',
				4: 'fourth',
			};
			const monthlyLabel = `Monthly on the ${isLast ? 'last' : replacements[weekOfMonth]} ${day}`;

			return monthlyLabel;
		}

		return option.label;
	};

	const repeatScheduleOptions = ScheduleRepeatOptions.filter(option => option.id !== ScheduleRepeatTypesEnum.Never).map(
		option => ({
			...option,
			label: getScheduleRepeatTypeLabel(option),
		})
	);

	return (
		<RightBlackModal
			open={open}
			onClose={handleClose}
			headerTitle='Repeat Settings'
			modalProps={{
				component: 'form',
				onSubmit: form.handleSubmit(handleSave),
			}}
			submitButtonProps={{
				type: 'submit',
				loading: form.formState.isSubmitting,
			}}
		>
			<Box sx={{ py: '12px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '4px',
					}}
				>
					<Typography
						sx={{
							fontFamily: 'Montserrat, sans-serif',
							fontWeight: '600',
							fontSize: '15px',
							lineHeight: '21px',
						}}
					>
						Event repeats
					</Typography>
					<CoreRadioGroup
						row
						value={schedule.typeId}
						onChange={(_e, val) => {
							onTypeIdChange(val as ScheduleRepeatTypesEnum);
						}}
						controlSx={{
							'& .MuiFormControlLabel-root:not(:last-child)': {
								marginRight: '24px',
							},
						}}
					>
						{repeatScheduleOptions.map(option => (
							<RadioFormLabel
								key={option.id}
								label={option.label}
								control={<CoreRadio />}
								value={option.id}
								sx={{
									'& .MuiFormControlLabel-label': {
										fontWeight: '500',
										lineHeight: '21px',
									},
								}}
							/>
						))}
					</CoreRadioGroup>
				</Box>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '12px',
					}}
				>
					<Showable show={schedule.typeId === ScheduleRepeatTypesEnum.Weekly}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'Montserrat, sans-serif',
									fontWeight: '600',
									fontSize: '15px',
									lineHeight: '21px',
								}}
							>
								Every
							</Typography>
							<TextField
								{...register('interval', {
									type: 'number',
									passType: false,
									min: minWeekInterval,
									max: maxWeekInterval,
									step: 1,
									fallbackValue: 1,
								})}
								controlId={`repeatInterval`}
								controlSx={{
									mr: '12px',
									padding: 0,

									'& .MuiInputBase-root': {
										padding: 0,
									},
								}}
								sx={{
									maxWidth: '38px',

									'& .MuiInputBase-input': {
										maxHeight: '40px',
										padding: '10px 10px',
										color: Colors.white_a60,
										fontSize: '16px',
										fontWeight: '600',
										fontFamily: 'Montserrat, sans-serif',
										lineHeight: '19px',
										textAlign: 'center',
									},
								}}
							/>
							<Typography
								sx={{
									fontFamily: 'Montserrat, sans-serif',
									fontWeight: '600',
									fontSize: '15px',
									lineHeight: '21px',
								}}
							>
								week
							</Typography>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'Montserrat, sans-serif',
									fontWeight: '600',
									fontSize: '15px',
									lineHeight: '21px',
								}}
							>
								Repeat On
							</Typography>
							<CoreFormControl
								controlId={`repeatWeekDays`}
								controlSx={{
									px: '12px',
								}}
							>
								<Box display='flex' alignItems='center' flexWrap='wrap'>
									{map(DaysOfWeek, ({ id, abbr }) => (
										<CoreIconButton
											key={id}
											onClick={onDayOfWeekChange.bind(null, id)}
											type="button"
											data-enabled={schedule.dayOfWeekIds?.includes(id)}
											sx={{
												width: '40px',
												height: '40px',
												fontFamily: 'Montserrat, sans-serif',
												fontWeight: '600',
												fontSize: '15px',
												lineHeight: '21px',
												color: schedule.dayOfWeekIds?.includes(id) ? Colors.black_a90 : Colors.white,
												bgcolor: schedule.dayOfWeekIds?.includes(id) ? Colors.white : Colors.white_a5,

												'&:not(:last-child)': {
													mr: '4px',
												},

												'&:hover, &.Mui-focusVisible, &:active': {
													boxShadow: `0 0 2px ${Colors.gray}`,
													bgcolor: schedule.dayOfWeekIds?.includes(id) ? Colors.white_a90 : Colors.white_a10,
												},
											}}
										>
											{abbr}
										</CoreIconButton>
									))}
								</Box>
							</CoreFormControl>
						</Box>
					</Showable>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '4px',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Montserrat, sans-serif',
								fontWeight: '600',
								fontSize: '15px',
								lineHeight: '21px',
							}}
						>
							Repeat
						</Typography>
						<CoreRadioGroup
							row
							value={state.endDateType}
							onChange={(_e, val: string) => {
								onRepeatUntilChange(val as RepeatUntilEnum);
							}}
							controlSx={{
								'& .MuiFormControlLabel-root:not(:last-child)': {
									marginRight: '48px',
								},
							}}
						>
							<RadioFormLabel
								label='Forever'
								control={<CoreRadio />}
								value={RepeatUntilEnum.Forever}
								sx={{
									'& .MuiFormControlLabel-label': {
										fontWeight: '500',
										lineHeight: '21px',
									},
								}}
							/>
							<RadioFormLabel
								label='Until certain date'
								control={<CoreRadio />}
								value={RepeatUntilEnum.After}
								sx={{
									'& .MuiFormControlLabel-label': {
										fontWeight: '500',
										lineHeight: '21px',
									},
								}}
							/>
						</CoreRadioGroup>
					</Box>

					<Showable show={state.endDateType === RepeatUntilEnum.After}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
							}}
						>
							<EventDateTimePicker
								{...register('endDate')}
								controlId='repeatUntilDate'
								onChange={date => form.setValue('endDate', date ?? undefined)}
								controlSx={{ minWidth: '288px' }}
								disablePast
								minDate={eventStartDate}
								timezone={tzid}
								closeOnChange
								hideTimeInput
							/>
						</Box>
					</Showable>
				</Box>
			</Box>
		</RightBlackModal>
	);
};
